import { XIcon } from "lucide-react"

import { FormRichText } from "@/components/forms/FormRichText"
import { creatorHandle } from "@/components/tableKit"
import { Heading } from "@/components/typography"
import { Button } from "@/components/ui/button"
import { getCurrencySymbol } from "@/shared/currency"
import type { Currency, Talent } from "@/shared/types"

export type PitchCartItem = {
    talent: Talent
    price: number
    notes?: Record<string, unknown>
}

export type PitchCartProps = {
    items: PitchCartItem[]
    currency: Currency
    submitting?: boolean
    onRemove: (talentId: string) => void
    onNotesChange: (talentId: string, notes: Record<string, unknown>) => void
    onSubmit: () => void
}

/** Pitching cart rail; New Booking uses BookingSummary. */
export function PitchCart({
    items,
    currency,
    submitting,
    onRemove,
    onNotesChange,
    onSubmit,
}: PitchCartProps) {
    const count = items.length
    const submitLabel =
        count === 0
            ? "Submit Pitch"
            : count === 1
                ? "Submit Pitch – 1 Creator"
                : `Submit Pitch – ${count} Creators`
    const symbol = getCurrencySymbol(currency)

    return (
        <div className="space-y-6 p-4">
            <Heading type="h3" tag="h2">
                Pitch Talent
            </Heading>
            {items.map((item) => {
                const handle = creatorHandle(item.talent)
                const displayName = handle ? `@${handle.replace(/^@/, "")}` : item.talent.name || "Creator"
                const imgUrl = item.talent.imgUrl || item.talent.manualImgUrl
                return (
                    <div key={item.talent.id} className="space-y-3">
                        {/* Avatar, name/price, and remove sit on one cart row. */}
                        <div className="flex items-start gap-2">
                            <div
                                className="size-10 shrink-0 rounded-md bg-muted bg-cover bg-center bg-no-repeat"
                                style={imgUrl ? { backgroundImage: `url(${imgUrl})` } : undefined}
                                role="img"
                                aria-label={displayName}
                            />
                            <div className="min-w-0 flex-1">
                                <div className="truncate font-bold">{displayName}</div>
                                <div className="text-sm text-muted-foreground">
                                    {symbol}
                                    {item.price.toLocaleString()}
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-xs"
                                aria-label={`Remove ${displayName}`}
                                onClick={() => onRemove(item.talent.id)}
                            >
                                <XIcon />
                            </Button>
                        </div>
                        <FormRichText
                            label="Notes for Brand"
                            value={item.notes}
                            onChange={(notes) => onNotesChange(item.talent.id, notes)}
                            inputClassName="bg-background"
                        />
                    </div>
                )
            })}
            <Button
                type="button"
                className="w-full"
                size="lg"
                disabled={count === 0 || submitting}
                onClick={onSubmit}
            >
                {submitting ? "Submitting…" : submitLabel}
            </Button>
        </div>
    )
}
